import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { ActivityProgressBadge } from './ActivityProgressBadge';
import { OperativeActivity, getProgress, getStepTitlesForActivity } from '../../services/operativeActivities';
import { CheckCircle2, Circle, Lock, Calendar, User, FileText, Briefcase, Edit, ChevronRight, ChevronDown, Tag, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ActivityDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: OperativeActivity | null;
  onStepComplete: (activityId: string, stepIndex: number) => void;
  onEdit?: (activity: OperativeActivity) => void;
}
export function ActivityDetailModal({
  isOpen,
  onClose,
  activity,
  onStepComplete,
  onEdit
}: ActivityDetailModalProps) {
  const [justCompleted, setJustCompleted] = useState<number | null>(null);
  const [allDone, setAllDone] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});
  if (!activity) return null;
  const progress = getProgress(activity);
  const titles = getStepTitlesForActivity(activity);
  const handleCompleteStep = (stepIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onStepComplete(activity.id, stepIndex);
    setJustCompleted(stepIndex);
    setTimeout(() => setJustCompleted(null), 1200);
    const remainingUncompleted = activity.stepsCompleted.filter((s, i) => !s && i !== stepIndex).length;
    if (remainingUncompleted === 0) {
      setTimeout(() => setAllDone(true), 600);
      setTimeout(() => {
        setAllDone(false);
        onClose();
      }, 2500);
    }
  };
  const toggleStep = (index: number) => {
    setExpandedSteps(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  const formatDate = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={`Attività operativa — ${activity.title}`} size="2xl" data-id="element-314">
      <AnimatePresence mode="wait" data-id="element-315">
        {allDone ? <motion.div key="alldone" initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="flex flex-col items-center justify-center py-16" data-id="element-316">
            <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-317">
              <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-318" />
            </motion.div>
            <p className="text-lg font-semibold text-slate-800" data-id="element-319">
              Attività completata!
            </p>
            <p className="text-sm text-slate-500 mt-1" data-id="element-320">
              Tutti gli step sono stati completati con successo.
            </p>
          </motion.div> : <motion.div key="content" initial={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} data-id="element-321">
            <div className="space-y-6" data-id="element-322">
              {/* Info Header */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-323">
                <div data-id="element-324">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-325">
                    <Tag className="h-3 w-3" data-id="element-326" /> Tipo
                  </span>
                  <div className="mt-1" data-id="element-327">
                    <Badge variant={activity.activityType === 'standard' ? 'info' : 'warning'} size="sm" data-id="element-328">
                      {activity.activityType === 'standard' ? 'Standard' : 'Personalizzata'}
                    </Badge>
                  </div>
                </div>
                <div data-id="element-329">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-330">
                    <Briefcase className="h-3 w-3" data-id="element-331" /> Cliente
                  </span>
                  <p className="font-medium text-sm text-slate-900 mt-0.5" data-id="element-332">
                    {activity.clientName}
                  </p>
                </div>
                <div data-id="element-333">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-334">
                    <FileText className="h-3 w-3" data-id="element-335" /> Contratto
                  </span>
                  <p className="font-mono font-medium text-sm text-slate-900 mt-0.5" data-id="element-336">
                    {activity.contractNumber}
                  </p>
                </div>
                <div data-id="element-337">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-338">
                    <ShoppingBag className="h-3 w-3" data-id="element-339" /> Prodotto
                  </span>
                  <p className="font-medium text-sm text-slate-900 mt-0.5" data-id="element-340">
                    {activity.product || '—'}
                  </p>
                </div>
                <div data-id="element-341">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-342">
                    <User className="h-3 w-3" data-id="element-343" /> Operatore
                  </span>
                  <p className="font-medium text-sm text-slate-900 mt-0.5" data-id="element-344">
                    {activity.operator}
                  </p>
                </div>
                <div data-id="element-345">
                  <span className="text-xs font-semibold text-slate-400 uppercase flex items-center gap-1" data-id="element-346">
                    <Calendar className="h-3 w-3" data-id="element-347" /> Scadenza
                  </span>
                  <p className={`font-medium text-sm mt-0.5 ${progress.isOverdue ? 'text-red-600 font-bold' : 'text-slate-900'}`} data-id="element-348">
                    {formatDate(activity.deadline)}
                    {progress.isOverdue && <span className="ml-1 text-xs text-red-500" data-id="element-349">
                        (scaduta)
                      </span>}
                  </p>
                </div>
              </div>

              {/* Overall Progress */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100" data-id="element-350">
                <span className="text-sm font-semibold text-seform-navy" data-id="element-351">
                  Avanzamento complessivo
                </span>
                <div className="w-48" data-id="element-352">
                  <ActivityProgressBadge currentStep={progress.currentStep} totalSteps={progress.totalSteps} percentage={progress.percentage} isCompleted={progress.isCompleted} isOverdue={progress.isOverdue} size="md" data-id="element-353" />
                </div>
              </div>

              {/* Steps Timeline */}
              <div className="space-y-0" data-id="element-354">
                <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider mb-4" data-id="element-355">
                  Step dell'attività
                </h4>
                <div className="relative" data-id="element-356">
                  {titles.map((title, index) => {
                const isStepCompleted = activity.stepsCompleted[index];
                const isCurrentStep = !isStepCompleted && (index === 0 || activity.stepsCompleted[index - 1]);
                const isLocked = !isStepCompleted && index > 0 && !activity.stepsCompleted[index - 1];
                const wasJustCompleted = justCompleted === index;
                const isExpanded = expandedSteps[index];
                return <div key={index} className="relative flex gap-4 pb-6 last:pb-0" data-id="element-357">
                        {/* Connecting line */}
                        {index < titles.length - 1 && <div className={`absolute left-[15px] top-8 bottom-6 w-0.5 ${isStepCompleted ? 'bg-green-300' : 'bg-slate-200'}`} data-id="element-358" />}

                        {/* Step icon */}
                        <div className="relative z-10 flex-shrink-0" data-id="element-359">
                          {isStepCompleted || wasJustCompleted ? <motion.div initial={wasJustCompleted ? {
                      scale: 0
                    } : {
                      scale: 1
                    }} animate={{
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 20
                    }} className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center" data-id="element-360">
                              <CheckCircle2 className="h-5 w-5 text-green-500" data-id="element-361" />
                            </motion.div> : isCurrentStep ? <div className="w-8 h-8 rounded-full bg-seform-orange/10 border-2 border-seform-orange flex items-center justify-center" data-id="element-362">
                              <span className="text-xs font-bold text-seform-orange" data-id="element-363">
                                {index + 1}
                              </span>
                            </div> : <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center" data-id="element-364">
                              {isLocked ? <Lock className="h-3.5 w-3.5 text-slate-300" data-id="element-365" /> : <Circle className="h-3.5 w-3.5 text-slate-300" data-id="element-366" />}
                            </div>}
                        </div>

                        {/* Step content */}
                        <div className={`flex-1 rounded-lg border transition-all overflow-hidden ${isStepCompleted ? 'bg-green-50/50 border-green-100' : isCurrentStep ? 'bg-orange-50/50 border-seform-orange/20 shadow-sm' : 'bg-slate-50/50 border-slate-100'}`} data-id="element-367">
                          <div className="p-3 flex items-center justify-between cursor-pointer hover:bg-black/5 transition-colors" onClick={() => toggleStep(index)} data-id="element-368">
                            <div className="flex items-center gap-3" data-id="element-369">
                              <div className={`p-1 rounded-md ${isExpanded ? 'bg-slate-200/50' : ''}`} data-id="element-370">
                                {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" data-id="element-371" /> : <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-372" />}
                              </div>
                              <div data-id="element-373">
                                <span className={`text-xs font-semibold uppercase tracking-wider ${isStepCompleted ? 'text-green-600' : isCurrentStep ? 'text-seform-orange' : 'text-slate-400'}`} data-id="element-374">
                                  Step {index + 1}
                                </span>
                                <h5 className={`font-semibold text-sm ${isStepCompleted ? 'text-green-800' : isCurrentStep ? 'text-seform-navy' : 'text-slate-400'}`} data-id="element-375">
                                  {title}
                                </h5>
                              </div>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-376">
                              {isCurrentStep && <Button size="sm" onClick={e => handleCompleteStep(index, e)} endContent={<ChevronRight className="h-3.5 w-3.5" data-id="element-378" />} data-id="element-377">
                                  Completa step
                                </Button>}
                              {isStepCompleted && <Badge variant="success" size="sm" data-id="element-379">
                                  Completato
                                </Badge>}
                              {isLocked && <Badge variant="neutral" size="sm" data-id="element-380">
                                  Bloccato
                                </Badge>}
                            </div>
                          </div>

                          {/* Expandable Details */}
                          <AnimatePresence data-id="element-381">
                            {isExpanded && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} className="overflow-hidden" data-id="element-382">
                                <div className="p-4 pt-0 border-t border-slate-100/50 mt-2" data-id="element-383">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm" data-id="element-384">
                                    <div className="bg-white p-3 rounded-lg border border-slate-100" data-id="element-385">
                                      <h6 className="font-semibold text-slate-700 mb-2 flex items-center gap-1.5" data-id="element-386">
                                        <User className="h-3.5 w-3.5 text-seform-orange" data-id="element-387" />
                                        Cosa fa il consulente
                                      </h6>
                                      <ul className="list-disc list-inside text-slate-600 space-y-1 text-xs" data-id="element-388">
                                        <li data-id="element-389">
                                          Analisi preliminare della
                                          documentazione
                                        </li>
                                        <li data-id="element-390">Raccolta dati e informazioni</li>
                                        <li data-id="element-391">Stesura del documento finale</li>
                                        <li data-id="element-392">Verifica conformità normativa</li>
                                      </ul>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border border-slate-100" data-id="element-393">
                                      <h6 className="font-semibold text-slate-700 mb-2 flex items-center gap-1.5" data-id="element-394">
                                        <Briefcase className="h-3.5 w-3.5 text-blue-500" data-id="element-395" />
                                        Cosa fa il cliente
                                      </h6>
                                      <ul className="list-disc list-inside text-slate-600 space-y-1 text-xs" data-id="element-396">
                                        <li data-id="element-397">Fornisce planimetrie aggiornate</li>
                                        <li data-id="element-398">
                                          Compila il questionario informativo
                                        </li>
                                        <li data-id="element-399">Garantisce l'accesso ai locali</li>
                                        <li data-id="element-400">Firma la documentazione</li>
                                      </ul>
                                    </div>
                                  </div>
                                  <div className="mt-3 flex gap-4 text-xs font-medium text-slate-500 bg-slate-100/50 p-2 rounded-md" data-id="element-401">
                                    <div className="flex items-center gap-1" data-id="element-402">
                                      <Calendar className="h-3.5 w-3.5" data-id="element-403" />
                                      <span data-id="element-404">Inizio previsto: 10/05/2025</span>
                                    </div>
                                    <div className="flex items-center gap-1" data-id="element-405">
                                      <Calendar className="h-3.5 w-3.5" data-id="element-406" />
                                      <span data-id="element-407">Fine prevista: 20/05/2025</span>
                                    </div>
                                  </div>
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </div>
                      </div>;
              })}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-408">
                <Button variant="flat" color="default" onClick={onClose} data-id="element-409">
                  Chiudi
                </Button>
                <Button variant="flat" startContent={<Edit className="h-4 w-4" data-id="element-411" />} onClick={() => {
              if (onEdit) onEdit(activity);
            }} data-id="element-410">
                  Modifica
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </Modal>;
}